/**
 * ConfirmDialog - a branded replacement for window.confirm().
 *
 * Mirrors BEETS' component. Deleting a requirement can take a whole subtree
 * with it, so the consequence is spelled out in the dialog rather than left to
 * be discovered.
 */

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Stack from '@mui/material/Stack'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

type Severity = 'warning' | 'error' | 'info'

const SEVERITY = {
  warning: { icon: <WarningAmberIcon color="warning" />, color: 'warning' as const },
  error: { icon: <ErrorOutlineIcon color="error" />, color: 'error' as const },
  info: { icon: <InfoOutlinedIcon color="info" />, color: 'primary' as const },
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  severity = 'warning',
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  severity?: Severity
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const chosen = SEVERITY[severity]
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {chosen.icon}
          <span>{title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button onClick={onConfirm} color={chosen.color} variant="contained" disabled={busy}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
